if (!customElements.get('size-guide-controller')) {
  class SizeGuideController extends HTMLElement {
    currentUnit = 'cm';
    abortController = null;
    garmentTableData = null;

    connectedCallback() {
      this.disconnectListeners();
      this.abortController = new AbortController();
      this.currentUnit = this.getDefaultUnit();

      this.garmentTableData = this.readGarmentTableSources();

      this.renderGarmentSizePills();
      this.bindEvents();
      this.syncUnitButtons();
      this.syncPageUnitPanels();
      this.updateGarmentValues();
      this.updateModelStats();
    }

    getDefaultUnit() {
      const defaultUnit = (this.dataset.defaultUnit || 'cm').trim().toLowerCase();

      return defaultUnit === 'in' ? 'in' : 'cm';
    }

    disconnectedCallback() {
      this.disconnectListeners();
    }

    disconnectListeners() {
      this.abortController?.abort();
      this.abortController = null;
    }

    bindEvents() {
      const {signal} = this.abortController;

      this.querySelectorAll('.sg-unit-btn').forEach((button) => {
        button.addEventListener('click', this.handleUnitClick, {signal});
      });

      this.querySelectorAll('[data-sg-size-pills]').forEach((container) => {
        container.addEventListener('click', this.handleSizePillClick, {signal});
      });

      this.querySelectorAll('.sg-tab-link').forEach((link) => {
        link.addEventListener('click', this.handleTabLinkClick, {signal});
      });
    }

    readGarmentTableSources() {
      const sources = Array.from(this.querySelectorAll('[data-sg-garment-source]'));
      if (!sources.length) return null;

      const dataByUnit = {};

      sources.forEach((source, index) => {
        const unit = (source.dataset.unit || (index === 0 ? 'cm' : `source-${index}`)).trim() || 'cm';
        const parsedTable = this.parseGarmentTable(source.querySelector('table'));

        if (parsedTable) {
          dataByUnit[unit] = parsedTable;
        }
      });

      return Object.keys(dataByUnit).length ? dataByUnit : null;
    }

    parseGarmentTable(table) {
      if (!table) return null;

      const rows = Array.from(table.querySelectorAll('tr'))
        .map((row) => Array.from(row.querySelectorAll('th, td')).map((cell) => this.getCellText(cell)))
        .filter((cells) => cells.length > 1 && cells.some(Boolean));

      if (rows.length < 2) return null;

      const headers = rows[0];
      const measurementHeaders = headers.slice(1);
      const sizes = [];
      const rowsBySize = {};

      rows.slice(1).forEach((cells) => {
        const size = cells[0];
        if (!size) return;

        const measurements = measurementHeaders
          .map((label, index) => ({
            label: label || `Measurement ${index + 1}`,
            value: cells[index + 1] || ''
          }))
          .filter((item) => item.label || item.value);

        if (!measurements.length) return;

        if (!sizes.includes(size)) {
          sizes.push(size);
        }

        rowsBySize[size] = measurements;
      });

      return sizes.length ? {sizes, rowsBySize} : null;
    }

    getCellText(cell) {
      return (cell?.textContent || '').replace(/\s+/g, ' ').trim();
    }

    syncUnitButtons() {
      this.querySelectorAll('.sg-unit-btn').forEach((button) => {
        button.classList.toggle('active', button.dataset.unit === this.currentUnit);
      });
    }

    handleUnitClick = (event) => {
      this.currentUnit = event.currentTarget.dataset.unit || 'cm';
      this.syncUnitButtons();
      this.syncPageUnitPanels();
      this.renderGarmentSizePills();
      this.updateGarmentValues();
      this.updateModelStats();
    };

    handleSizePillClick = (event) => {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      const selectedPill = target?.closest('[data-size-pill]');
      if (!selectedPill || !event.currentTarget.contains(selectedPill)) return;

      this.querySelectorAll('[data-size-pill]').forEach((pill) => {
        pill.classList.remove('active');
      });

      selectedPill.classList.add('active');
      this.updateGarmentValues();
    };

    handleTabLinkClick = (event) => {
      event.preventDefault();

      const targetId = event.currentTarget.dataset.tabTarget;
      if (!targetId) return;

      const targetButton = this.querySelector(`[data-id="${targetId}"]`);
      if (!targetButton) return;

      targetButton.click();

      const drawerBody = this.closest('[data-drawer-scrolls]');
      if (drawerBody) drawerBody.scrollTop = 0;
    };

    syncPageUnitPanels() {
      this.querySelectorAll('[data-sg-page-unit-panel]').forEach((panel) => {
        const isActive = panel.dataset.sgPageUnitPanel === this.currentUnit;
        panel.hidden = !isActive;
        panel.style.display = isActive ? 'block' : 'none';
      });
    }

    getGarmentTableDataForCurrentUnit() {
      if (!this.garmentTableData) return null;

      return this.garmentTableData[this.currentUnit] || this.garmentTableData.cm || Object.values(this.garmentTableData)[0] || null;
    }

    renderGarmentSizePills() {
      const pillsContainer = this.querySelector('[data-sg-size-pills]');
      const tableData = this.getGarmentTableDataForCurrentUnit();

      if (!pillsContainer || !tableData) return;

      const currentSize = pillsContainer.querySelector('[data-size-pill].active')?.dataset.sizePill;
      const selectedSize = tableData.sizes.includes(currentSize) ? currentSize : tableData.sizes[0];
      const fragment = document.createDocumentFragment();

      pillsContainer.innerHTML = '';

      tableData.sizes.forEach((size) => {
        const pill = document.createElement('button');
        pill.className = `sg-size-pill${size === selectedSize ? ' active' : ''}`;
        pill.type = 'button';
        pill.dataset.sizePill = size;
        pill.textContent = size;
        fragment.append(pill);
      });

      pillsContainer.append(fragment);
    }

    updateGarmentValues() {
      const display = this.querySelector('[data-sg-garment-display]');
      const tableData = this.getGarmentTableDataForCurrentUnit();

      if (!display || !tableData) return;

      this.renderGarmentSizePills();

      const activeSizePill = this.querySelector('[data-size-pill].active');
      const size = activeSizePill?.dataset.sizePill || tableData.sizes[0];
      const measurements = tableData.rowsBySize[size] || [];
      const fragment = document.createDocumentFragment();

      display.innerHTML = '';

      measurements.forEach((measurement) => {
        const row = document.createElement('div');
        const label = document.createElement('span');
        const value = document.createElement('span');

        row.className = 'sg-garment-row';
        label.className = 'sg-garment-label';
        value.className = 'sg-garment-value';
        label.textContent = measurement.label;
        value.textContent = measurement.value || '--';
        row.append(label, value);
        fragment.append(row);
      });

      display.append(fragment);
    }

    updateModelStats() {
      this.querySelectorAll('.sg-model-stats [data-model-stat]').forEach((stat) => {
        const label = stat.dataset.modelStat;
        const valueElement = stat.querySelector('.sg-model-stat-value');
        if (!valueElement) return;

        let originalValue = stat.dataset.originalValue;

        if (!originalValue) {
          originalValue = valueElement.textContent.trim();
          if (originalValue) {
            stat.dataset.originalValue = originalValue;
          }
        }

        if (!originalValue || label === 'size') return;

        if (this.currentUnit === 'in') {
          valueElement.textContent = originalValue;
          return;
        }

        if (label === 'height') {
          this.updateHeightStat(valueElement, originalValue);
          return;
        }

        this.updateInchStat(valueElement, originalValue);
      });
    }

    updateHeightStat(valueElement, originalValue) {
      const match = originalValue.match(/(\d+)'(\d+)/);
      if (!match) return;

      const totalInches = Number.parseInt(match[1], 10) * 12 + Number.parseInt(match[2], 10);
      valueElement.textContent = `${Math.round(totalInches * 2.54)}cm`;
    }

    updateInchStat(valueElement, originalValue) {
      const numericValue = Number.parseFloat(originalValue);
      if (Number.isNaN(numericValue)) return;

      valueElement.textContent = `${Math.round(numericValue * 2.54 * 10) / 10}cm`;
    }
  }

  customElements.define('size-guide-controller', SizeGuideController);
}
